/**
 * Offline Queue - Streamlined implementation for offline operations
 *
 * This class provides a simple but robust offline operation queue that:
 * - Automatically queues operations when offline or on network errors
 * - Persists queued operations using pluggable storage adapters
 * - Automatically syncs operations when network connectivity returns
 * - Supports retry logic with configurable retry limits
 * - Provides cross-platform network detection
 *
 * @example
 * ```typescript
 * const queue = new OfflineQueue({
 *   maxRetries: 3,
 *   maxQueueSize: 100,
 *   storageKey: 'my-app-queue',
 *   storageAdapter: customStorageAdapter,
 *   networkDetector: customNetworkDetector
 * });
 *
 * // Set up the executor function
 * queue.setExecutor(async (service, method, params) => {
 *   return await api.call(service, method, params);
 * });
 *
 * // Add operations to the queue
 * await queue.add('favorites', 'addWord', { wordId: '123' });
 *
 * // Sync all queued operations
 * const result = await queue.sync();
 * console.log(`Synced: ${result.success} success, ${result.failed} failed`);
 * ```
 */

export interface QueuedOperation {
  id: string;
  service: string;
  method: string;
  params: Record<string, any>;
  timestamp: number;
  retries: number;
}

import { OfflineStorageAdapter } from "../types/offline.types";
import {
  NetworkDetectionManager,
  NetworkCleanupFunction,
} from "../utils/network-detection-manager";

export interface OfflineQueueConfig {
  maxRetries: number;
  maxQueueSize: number;
  storageKey: string;
  storageAdapter?: OfflineStorageAdapter;
  networkManager?: NetworkDetectionManager;
  /** Called when an operation has exhausted all retries and is permanently dropped */
  onPermanentFailure?: (service: string, method: string, error: unknown, params: Record<string, any>) => void;
  /** Called when autoSync (triggered by network reconnect) fails to drain the queue */
  onAutoSyncFailure?: (error: unknown, queueLength: number) => void;
  /** Called when an operation is queued (offline or network error) — use for breadcrumbs */
  onOperationQueued?: (service: string, method: string, params: Record<string, any>, queueLength: number) => void;
}

export class OfflineQueue {
  private operations: QueuedOperation[] = [];
  private config: OfflineQueueConfig;
  private storage: OfflineStorageAdapter;
  private networkManager: NetworkDetectionManager;
  private networkCleanup?: NetworkCleanupFunction;
  // Tracks the in-flight sync so concurrent callers await the same run rather
  // than racing the operations array and double-executing queued writes.
  private syncPromise: Promise<{ success: number; failed: number }> | null =
    null;

  constructor(config: Partial<OfflineQueueConfig> = {}) {
    this.config = {
      maxRetries: 3,
      maxQueueSize: 100,
      storageKey: "ling-offline-queue",
      ...config,
    };

    // Initialize storage adapter (custom or default)
    this.storage = config.storageAdapter || this.createDefaultStorageAdapter();

    // Use provided network detection manager
    this.networkManager = config.networkManager!; // Asserted non-null because SDK creates it

    this.loadFromStorage().catch((error) => {
      console.warn("Failed to load initial queue from storage:", error);
    });
    this.setupNetworkDetection();
  }

  /**
   * Add operation to queue
   */
  async add(
    service: string,
    method: string,
    params: Record<string, any>
  ): Promise<string> {
    const operation: QueuedOperation = {
      id: this.generateId(),
      service,
      method,
      params,
      timestamp: Date.now(),
      retries: 0,
    };

    // If online, try to execute immediately
    if (this.networkManager.isOnline) {
      try {
        await this.executeOperation(operation);
        return operation.id;
      } catch (error) {
        // If immediate execution fails, add to queue
        console.log("Operation failed, adding to queue:", operation.id);
      }
    }

    // Add to queue
    if (this.operations.length >= this.config.maxQueueSize) {
      throw new Error("Queue is full");
    }

    this.operations.push(operation);
    await this.saveToStorage();
    this.config.onOperationQueued?.(operation.service, operation.method, operation.params, this.operations.length);
    return operation.id;
  }

  /**
   * Sync all queued operations.
   *
   * Re-entrant safe: if a sync is already in flight, returns the same promise
   * instead of starting a second pass, preventing double-executed writes.
   */
  sync(): Promise<{ success: number; failed: number }> {
    // Fail fast when offline, before the in-flight check, so an offline call
    // always rejects rather than attaching to a run started while online.
    if (!this.networkManager.isOnline) {
      return Promise.reject(new Error("Cannot sync while offline"));
    }

    // Not async: return the shared promise by reference so concurrent callers
    // get the identical object, not an async-wrapped copy of it.
    if (this.syncPromise) {
      return this.syncPromise;
    }

    this.syncPromise = this.performSync().finally(() => {
      this.syncPromise = null;
    });
    return this.syncPromise;
  }

  /**
   * Internal: performs the actual sync pass. Callers must go through sync()
   * so the in-flight guard applies.
   */
  private async performSync(): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;
    const remaining: QueuedOperation[] = [];

    for (const operation of this.operations) {
      try {
        await this.executeOperation(operation);
        success++;
        // Gentle throttle between ops to avoid bursting the server on large flushes
        await new Promise<void>((resolve) => setTimeout(resolve, 250));
      } catch (error) {
        operation.retries++;
        if (operation.retries < this.config.maxRetries) {
          remaining.push(operation);
          // Operation failed but will be retried later - don't count as failed yet
        } else {
          console.error("Operation failed permanently:", operation.id, error);
          this.config.onPermanentFailure?.(operation.service, operation.method, error, operation.params);
          failed++;
        }
      }
    }

    this.operations = remaining;
    await this.saveToStorage();

    return { success, failed };
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      isOnline: this.networkManager.isOnline,
      queuedOperations: this.operations.length,
      operations: this.operations.map((op) => ({
        id: op.id,
        service: op.service,
        method: op.method,
        retries: op.retries,
        timestamp: op.timestamp,
      })),
    };
  }

  /**
   * Clear all operations
   */
  clear(): void {
    this.operations = [];
    this.saveToStorage().catch((error) => {
      console.warn("Failed to clear queue storage:", error);
    });
  }

  /**
   * Clean up all resources and listeners
   */
  destroy(): void {
    // Clean up our network listener (but don't destroy the shared manager)
    if (this.networkCleanup) {
      this.networkCleanup();
      this.networkCleanup = undefined;
    }

    // Clear in-memory operations but preserve in storage for next app start
    // This ensures queued operations survive app restarts
    // Note: We don't destroy the networkManager here as it's shared across the SDK
    this.operations = [];
  }

  /**
   * Set operation executor (to be called by SDK)
   */
  setExecutor(
    executor: (
      service: string,
      method: string,
      params: Record<string, any>
    ) => Promise<any>
  ): void {
    this.executeOperation = async (operation: QueuedOperation) => {
      return await executor(
        operation.service,
        operation.method,
        operation.params
      );
    };
  }

  private executeOperation: (operation: QueuedOperation) => Promise<any> =
    async () => {
      throw new Error("Executor not set");
    };

  private generateId(): string {
    return `op_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private async saveToStorage(): Promise<void> {
    try {
      const result = this.storage.setItem(
        this.config.storageKey,
        JSON.stringify(this.operations)
      );
      if (result instanceof Promise) {
        await result;
      }
    } catch (error) {
      console.warn("Failed to save queue to storage:", error);
    }
  }

  private async loadFromStorage(): Promise<void> {
    try {
      const result = this.storage.getItem(this.config.storageKey);
      const stored = result instanceof Promise ? await result : result;
      if (stored) {
        this.operations = JSON.parse(stored);
      }
    } catch (error) {
      console.warn("Failed to load queue from storage:", error);
      this.operations = [];
    }
  }

  /**
   * Creates default storage adapter based on environment
   */
  private createDefaultStorageAdapter(): OfflineStorageAdapter {
    return {
      getItem: (key: string) => {
        if (typeof localStorage !== "undefined") {
          return localStorage.getItem(key);
        }
        return null;
      },
      setItem: (key: string, value: string) => {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(key, value);
        }
      },
      removeItem: (key: string) => {
        if (typeof localStorage !== "undefined") {
          localStorage.removeItem(key);
        }
      },
    };
  }

  /**
   * Set up network state monitoring and auto-sync
   */
  private setupNetworkDetection(): void {
    this.networkCleanup = this.networkManager.addListener(
      (isOnline: boolean) => {
        if (isOnline) {
          console.log("Back online - automatically syncing queued operations");
          this.autoSync();
        } else {
          console.log("Gone offline - operations will be queued");
        }
      }
    );
  }

  /**
   * Automatically sync queued operations when back online
   */
  private autoSync(): void {
    if (this.operations.length > 0) {
      this.sync()
        .then((result) => {
          console.log(
            `Auto-sync completed: ${result.success} success, ${result.failed} failed`
          );
        })
        .catch((error) => {
          console.error("Auto-sync failed:", error);
          this.config.onAutoSyncFailure?.(error, this.operations.length);
        });
    }
  }
}
